import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

const BOOKS_COLLECTION = 'books';
const WISHLIST_COLLECTION = 'wishlist';
const MASTER_DATA_COLLECTION = 'masterData';

// Book CRUD operations
export const bookService = {
  // Create a new book
  async createBook(bookData, userId) {
    try {
      console.log('=== CREATE BOOK SERVICE DEBUG ===');
      console.log('Input bookData:', JSON.stringify(bookData, null, 2));
      console.log('UserId:', userId);
      
      // Validate input data
      if (!bookData) {
        throw new Error('bookData is required');
      }
      if (!userId) {
        throw new Error('userId is required');
      }
      
      // Deep clone and sanitize the data
      const sanitizedData = JSON.parse(JSON.stringify(bookData));
      
      const bookWithUser = {
        ...sanitizedData,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('Final bookWithUser data:', JSON.stringify(bookWithUser, null, 2));
      
      const result = await addDoc(collection(db, BOOKS_COLLECTION), bookWithUser);
      console.log('Create book result:', result);
      console.log('=== CREATE BOOK SERVICE END ===');
      return result;
    } catch (error) {
      console.error('=== CREATE BOOK SERVICE ERROR ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Input bookData:', JSON.stringify(bookData, null, 2));
      console.error('UserId:', userId);
      console.error('=== END CREATE BOOK ERROR ===');
      throw error;
    }
  },

  // Get all books for a user
  async getBooks(userId) {
    const q = query(
      collection(db, BOOKS_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const books = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Ensure all fields have default values and consistent naming
        author: data.author || '',
        isbn: data.isbn || '',
        title: data.title || '',
        publisher: data.publisher || '',
        series: data.series || '',
        genre: data.genre || '',
        type: data.type || '',
        language: data.language || '',
        imageUrl: data.imageUrl || '',
        qty: data.qty || 1,
        price: data.price || 0,
        source: data.source || '',
        note: data.note || '-',
        createdDate: data.createdDate || ''
      };
    });
    
    // Sort by createdAt in JavaScript instead of Firestore
    return books.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toDate ? b.createdAt.toDate() - a.createdAt.toDate() : 0;
    });
  },

  // Get distinct series for a user
  async getDistinctSeries(userId) {
    const q = query(
      collection(db, BOOKS_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const seriesSet = new Set();
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.series && data.series.trim()) {
        seriesSet.add(data.series.trim());
      }
    });
    
    return Array.from(seriesSet).sort();
  },

  // Get a single book by ID
  async getBookById(bookId) {
    try {
      console.log('=== GET BOOK BY ID SERVICE DEBUG ===');
      console.log('BookId:', bookId);
      
      if (!bookId) {
        throw new Error('Book ID is required');
      }
      
      const docRef = doc(db, BOOKS_COLLECTION, bookId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('Raw Firestore data:', JSON.stringify(data, null, 2));
        
        const processedData = { 
          id: docSnap.id, 
          ...data,
          // Ensure all fields have default values
          author: data.author || '',
          isbn: data.isbn || '',
          title: data.title || '',
          publisher: data.publisher || '',
          genre: data.genre || '',
          type: data.type || '',
          language: data.language || '',
          imageUrl: data.imageUrl || '',
          qty: data.qty || 1,
          price: data.price || 0,
          source: data.source || '',
          note: data.note || '-',
          createdDate: data.createdDate || ''
        };
        
        console.log('Processed book data:', JSON.stringify(processedData, null, 2));
        console.log('=== GET BOOK BY ID SERVICE END ===');
        return processedData;
      }
      
      console.log('Book not found');
      console.log('=== GET BOOK BY ID SERVICE END ===');
      return null;
    } catch (error) {
      console.error('=== GET BOOK BY ID SERVICE ERROR ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('BookId:', bookId);
      console.error('=== END GET BOOK BY ID ERROR ===');
      throw error;
    }
  },

  // Update a book
  async updateBook(bookId, bookData) {
    try {
      console.log('=== UPDATE BOOK SERVICE DEBUG ===');
      console.log('BookId:', bookId);
      console.log('Input bookData:', JSON.stringify(bookData, null, 2));
      
      // Validate input data
      if (!bookId) {
        throw new Error('bookId is required');
      }
      if (!bookData) {
        throw new Error('bookData is required');
      }
      
      // Deep clone and sanitize the data
      const sanitizedData = JSON.parse(JSON.stringify(bookData));
      
      const docRef = doc(db, BOOKS_COLLECTION, bookId);
      const updateData = {
        ...sanitizedData,
        updatedAt: serverTimestamp()
      };
      
      console.log('Final updateData:', JSON.stringify(updateData, null, 2));
      
      const result = await updateDoc(docRef, updateData);
      console.log('Update book result:', result);
      console.log('=== UPDATE BOOK SERVICE END ===');
      return result;
    } catch (error) {
      console.error('=== UPDATE BOOK SERVICE ERROR ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('BookId:', bookId);
      console.error('Input bookData:', JSON.stringify(bookData, null, 2));
      console.error('=== END UPDATE BOOK ERROR ===');
      throw error;
    }
  },

  // Delete a book
  async deleteBook(userId, bookId) {
    const docRef = doc(db, BOOKS_COLLECTION, bookId);
    return await deleteDoc(docRef);
  },

  // Search books
  async searchBooks(userId, searchTerm) {
    const q = query(
      collection(db, BOOKS_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const books = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by createdAt in JavaScript
    const sortedBooks = books.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toDate ? b.createdAt.toDate() - a.createdAt.toDate() : 0;
    });
    
    if (!searchTerm) return sortedBooks;
    
    return sortedBooks.filter(book => 
      book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.publisher?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.author?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
};

// Wishlist operations
export const wishlistService = {
  // Create a new wishlist item
  async createWishItem(wishData, userId) {
    try {
      if (!wishData) {
        throw new Error('wishData is required');
      }
      if (!userId) {
        throw new Error('userId is required');
      }
      
      const sanitizedData = JSON.parse(JSON.stringify(wishData));
      
      const wishWithUser = {
        ...sanitizedData,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const result = await addDoc(collection(db, WISHLIST_COLLECTION), wishWithUser);
      return result;
    } catch (error) {
      console.error('Error creating wish item:', error);
      throw error;
    }
  },

  // Get all wishlist items for a user
  async getWishItems(userId) {
    const q = query(
      collection(db, WISHLIST_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const wishItems = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        author: data.author || '',
        isbn: data.isbn || '',
        title: data.title || '',
        publisher: data.publisher || '',
        genre: data.genre || '',
        type: data.type || '',
        language: data.language || '',
        imageUrl: data.imageUrl || '',
        price: data.price || 0,
        note: data.note || '-',
        createdDate: data.createdDate || ''
      };
    });
    
    return wishItems.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toDate ? b.createdAt.toDate() - a.createdAt.toDate() : 0;
    });
  },

  // Get a single wishlist item by ID
  async getWishItemById(wishId) {
    const docRef = doc(db, WISHLIST_COLLECTION, wishId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        author: data.author || '',
        isbn: data.isbn || '',
        title: data.title || '',
        publisher: data.publisher || '',
        genre: data.genre || '',
        type: data.type || '',
        language: data.language || '',
        imageUrl: data.imageUrl || '',
        price: data.price || 0,
        note: data.note || '-',
        createdDate: data.createdDate || ''
      };
    } else {
      throw new Error('Wish item not found');
    }
  },

  // Update a wishlist item
  async updateWishItem(wishId, wishData) {
    try {
      const sanitizedData = JSON.parse(JSON.stringify(wishData));
      
      const updateData = {
        ...sanitizedData,
        updatedAt: serverTimestamp()
      };
      
      const docRef = doc(db, WISHLIST_COLLECTION, wishId);
      const result = await updateDoc(docRef, updateData);
      return result;
    } catch (error) {
      console.error('Error updating wish item:', error);
      throw error;
    }
  },

  // Delete a wishlist item
  async deleteWishItem(wishId) {
    const docRef = doc(db, WISHLIST_COLLECTION, wishId);
    return await deleteDoc(docRef);
  },

  // Convert wishlist item to book (and delete from wishlist)
  async convertWishToBook(wishId, userId) {
    try {
      // Get the wish item
      const wishItem = await this.getWishItemById(wishId);
      
      // Create a new book from the wish item
      const bookData = {
        ...wishItem,
        // Add default values for book-specific fields
        qty: 1,
        source: '',
        // Remove wishlist-specific fields
        id: undefined,
        createdAt: undefined,
        updatedAt: undefined
      };
      
      // Create the book
      await bookService.createBook(bookData, userId);
      
      // Delete the wish item
      await this.deleteWishItem(wishId);
      
      return true;
    } catch (error) {
      console.error('Error converting wish to book:', error);
      throw error;
    }
  },

  // Search wishlist items
  async searchWishItems(userId, searchTerm) {
    const q = query(
      collection(db, WISHLIST_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const wishItems = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const sortedItems = wishItems.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toDate ? b.createdAt.toDate() - a.createdAt.toDate() : 0;
    });
    
    if (!searchTerm) return sortedItems;
    
    return sortedItems.filter(item => 
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.publisher?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.author?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
};

// Master data operations
export const masterDataService = {
  // Get master data for autocomplete
  async getMasterData() {
    const querySnapshot = await getDocs(collection(db, MASTER_DATA_COLLECTION));
    const masterData = {
      publishers: [],
      genres: [],
      types: [],
      languages: [],
      sources: []
    };
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.type && data.values) {
        masterData[data.type] = data.values;
      }
    });
    
    return masterData;
  },

  // Initialize master data (run once to set up)
  async initializeMasterData() {
    const masterData = {
      publishers: [
        // Major Thai Publishers
        'นานมีบุ๊คส์ (Nanmeebooks)', 'อมรินทร์ (Amarin)', 'ซีเอ็ด (SE-ED)', 'เอ็มไอเอส (MIS)', 'ไอแอลแอล (ILL)', 
        'แพรวสำนักพิมพ์ (Praew Publishing)', 'สำนักพิมพ์มติชน (Matichon Publishing)', 
        'สำนักพิมพ์สยาม (Siam Publishing)', 'สำนักพิมพ์แสงดาว (Saengdao Publishing)', 
        'สำนักพิมพ์ผีเสื้อ (Pisuea Publishing)', 'สำนักพิมพ์ดอกหญ้า (Dokya Publishing)', 
        'สำนักพิมพ์เรือนแก้ว (Ruenkaew Publishing)', 'สำนักพิมพ์ต้นฉบับ (Tonchabap Publishing)', 
        'สำนักพิมพ์ปิยะ (Piya Publishing)', 'สำนักพิมพ์ประพันธ์สาส์น (Praphan Sarn Publishing)', 
        'สำนักพิมพ์บรรณกิจ (Bannakit Publishing)', 'สำนักพิมพ์บรรณารักษ์ (Bannaruk Publishing)',
        'สำนักพิมพ์บรรณสาร (Bannasan Publishing)', 'สำนักพิมพ์บรรณศิลป์ (Bannasin Publishing)', 
        'สำนักพิมพ์บรรณสาส์น (Bannasarn Publishing)',
        
        // Chinese and Japanese Novels
        'แจ่มใส (Jamsai Publishing)', 'เอ็นเธอร์บุ๊คส์ (Enter Books)', 
        'สยามอินเตอร์บุ๊คส์ (Siam Inter Books)', 'อัมรินทร์พริ้นติ้งแอนด์พับลิชชิ่ง (Amarin Printing and Publishing)',
        'สำนักพิมพ์พับลิชชิ่ง (Publishing)',
        
        // Manga and Light Novels
        'สยามอินเตอร์คอมิกส์ (Siam Inter Comics)', 'เนชั่นเอ็ดดูเทนเมนท์ (Nation Edutainment)',
        'บงกช พับลิชชิ่ง (Bongkoch Publishing)', 'วิบูลย์กิจ (Vibulkij)',
        'รักพิมพ์ พับลิชชิ่ง (Luckpim Publishing)', 'ฟีนิกซ์ เน็กซ์ (Phoenix Next)',
        
        // Digital Platforms and Modern Publishers
        'Meb (Mobile E-books)', 'จอยลดา (Joylada)', 'ธัญวลัย (Tunwalai)', 
        'EverY (ในเครือแจ่มใส)', 'นาบู พับลิชชิ่ง (Nabu Publishing)',
        'โรส พับลิชชิ่ง (Rose Publishing)', 'เฮอร์มิท บุ๊คส์ (Hermit Books)',
        'พิทบูลเล็ก (Pibunlek)', 'มีดีส์ (MeeDeeS)', 'อมรินทร์ คอมิกส์ (Amarin Comics)',
        'สปริง บุ๊คส์ (Spring Books)'
      ],
      genres: [
        'นิยาย', 'วรรณกรรม', 'หนังสือเด็ก', 'หนังสือเรียน', 'หนังสือวิชาการ',
        'นิยายรัก', 'นิยายแฟนตาซี', 'นิยายสยองขวัญ', 'นิยายสืบสวน', 'นิยายประวัติศาสตร์',
        'วรรณกรรมคลาสสิก', 'วรรณกรรมร่วมสมัย', 'กวีนิพนธ์', 'เรื่องสั้น', 'สารคดี',
        'หนังสือภาพ', 'หนังสือการ์ตูน', 'หนังสือคู่มือ', 'หนังสืออ้างอิง', 'หนังสือชีวประวัติ'
      ],
      types: ['หนังสือกระดาษ', 'หนังสืออิเล็กทรอนิกส์ (E-book)'],
      languages: ['ไทย', 'อังกฤษ'],
      sources: ['ร้านหนังสือ', 'ออนไลน์', 'งานหนังสือ', 'มือสอง', 'ห้องสมุด', 'เพื่อน', 'ครอบครัว']
    };

    for (const [key, values] of Object.entries(masterData)) {
      await addDoc(collection(db, MASTER_DATA_COLLECTION), {
        type: key,
        values: values
      });
    }
  }
}; 